import { useCallback } from 'react';
import { creatorbaseQueries, getPaginatedIdQueryParam, instagramQueries } from '@round/api';
import { useQueryClient } from '@tanstack/react-query';
import {
    useInstagramUserImageListQueries,
    useInstagramUserListQueries,
    useInstagramUserStatsCacheListQueries,
} from '@round/platforms/instagram';
import usePublicReportId from 'Modules/Plans/PublicReports/usePublicReportId';
import { isNumber } from 'utility/utility';
import uniq from 'lodash/uniq';
import { backupPostsStatuses } from 'Modules/Plans/Posts/helpers';
import { useInfinitePosts, usePostsTotals } from '@round/creatorbase-helpers/api-hooks';

// Serves as both the posts page size and the account-id batch size, so one posts page never needs
// more than one batch of account requests.
const BATCH_SIZE = 50;

// fetchImmediate needs every post in one response, so it reads far past a table page.
const FETCH_IMMEDIATE_PAGE_SIZE = 1000;

// Keeps infinite posts, totals, and imperative posts aligned in terms of params
const buildPostsParams = (campaignId: number | undefined, publicReportId: string | undefined) => ({
    campaign_id: campaignId?.toString(),
    ordering: '-view_count' as const,
    exclude_statuses: backupPostsStatuses.join(','),
    platform: 'instagram' as const,
    publicReportId,
});

// Totals must cover exactly the list's filtered set, so they're derived from the list params rather
// than built independently.
const buildPostsTotalsParams = (campaignId: number | undefined, publicReportId: string | undefined) => {
    const { ordering, ...params } = buildPostsParams(campaignId, publicReportId);
    return params;
};

// One request per batch of user ids, with page_size following the batch so no batch is truncated.
// The declarative reads below and the imperative fetchImmediate prime build their params here, so
// equal id sets hit identical query keys and share cache entries.
const buildUserParams = (userIds: number[], publicReportId: string | undefined) => ({
    id: userIds.join(','),
    page_size: userIds.length,
    publicReportId,
});

const buildUserImageParams = (userIds: number[], publicReportId: string | undefined) => ({
    ids: userIds,
    page_size: userIds.length,
    publicReportId,
});

const buildUserStatsCacheParams = (userIds: number[], publicReportId: string | undefined) => ({
    user_id: userIds,
    page_size: userIds.length,
    publicReportId,
});

const getUserIds = (posts: { instagram_details: { account_id: number | null } | null }[]) =>
    uniq(posts.map((post) => post.instagram_details?.account_id).filter(isNumber));

// Rows are partial until every page is loaded, so the footers always read API totals rather than
// reducing over the rendered rows.
const collectLoadingPageIndexes = (results: { isLoading: boolean }[]) =>
    new Set(results.flatMap((result, index) => (result.isLoading ? [index] : [])));

export function usePublicReportInstagramPosts(
    campaignId: number | undefined,
    { enabled = true }: { enabled?: boolean } = {}
) {
    const publicReportId = usePublicReportId();
    const queryClient = useQueryClient();
    const isReady = enabled && Boolean(publicReportId) && Boolean(campaignId);

    const infinitePosts = useInfinitePosts(
        { ...buildPostsParams(campaignId, publicReportId), page_size: BATCH_SIZE },
        { enabled: isReady }
    );

    // isLoading, not isFetching: a background refetch would otherwise skeleton every footer while the
    // row cells keep their values.
    const { data: postsTotals, isLoading: areTotalsLoading } = usePostsTotals(
        buildPostsTotalsParams(campaignId, publicReportId),
        { enabled: isReady }
    );

    const postPages = (infinitePosts.data?.pages ?? []).map((page) => page.results);
    const posts = postPages.flat();

    // User ids are batched by posts page, so loading another page never changes an earlier page's
    // query key. Every page issues a query — disabled when the page has no accounts — so a query's
    // index is its page index.
    const userIdBatches = postPages.map(getUserIds);

    const { usersData, loadingUserPageIndexes } = useInstagramUserListQueries(
        userIdBatches.map((ids) => ({
            params: buildUserParams(ids, publicReportId),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                usersData: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((user) => [user.id, user])
                ),
                loadingUserPageIndexes: collectLoadingPageIndexes(results),
            }),
        }
    );

    const { userImagesData, loadingImagePageIndexes } = useInstagramUserImageListQueries(
        userIdBatches.map((ids) => ({
            params: buildUserImageParams(ids, publicReportId),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                userImagesData: new Map(
                    results.flatMap((result) => result.data ?? []).map((image) => [image.user_id, image])
                ),
                loadingImagePageIndexes: collectLoadingPageIndexes(results),
            }),
        }
    );

    const { userStatsData, loadingStatsPageIndexes } = useInstagramUserStatsCacheListQueries(
        userIdBatches.map((ids) => ({
            params: buildUserStatsCacheParams(ids, publicReportId),
            config: { enabled: ids.length > 0 },
        })),
        {
            combine: (results) => ({
                userStatsData: new Map(
                    results.flatMap((result) => result.data?.results ?? []).map((stats) => [stats.user_id, stats])
                ),
                loadingStatsPageIndexes: collectLoadingPageIndexes(results),
            }),
        }
    );

    const pageIndexByPostId = new Map(
        postPages.flatMap((pagePosts, pageIndex) => pagePosts.map((post) => [post.id, pageIndex] as const))
    );
    // A row's loading state is its own page's, so a later page's fetch never skeletons earlier rows.
    const getIsAccountDataLoading = (post: { id: string }) => {
        const pageIndex = pageIndexByPostId.get(post.id);
        if (pageIndex === undefined) {
            return false;
        }

        return (
            loadingUserPageIndexes.has(pageIndex) ||
            loadingImagePageIndexes.has(pageIndex) ||
            loadingStatsPageIndexes.has(pageIndex)
        );
    };

    // Copy-to-clipboard needs every post plus its account data, however few pages the table has
    // loaded, so it primes its own full-set query keys. ensureQueryData reuses the shared cache and
    // only fetches what is missing.
    const fetchImmediate = useCallback(async () => {
        if (!campaignId) {
            return null;
        }

        const postsResponse = await queryClient.ensureQueryData(
            creatorbaseQueries.posts.options.list({
                ...buildPostsParams(campaignId, publicReportId),
                page_size: FETCH_IMMEDIATE_PAGE_SIZE,
            })
        );
        const posts = postsResponse.results;

        const userIds = getUserIds(posts);
        if (!userIds.length) {
            return { posts, users: [], userStats: [] };
        }

        try {
            const [users, userStats] = await Promise.all([
                Promise.all(
                    getPaginatedIdQueryParam(userIds, BATCH_SIZE).map((ids) =>
                        queryClient.ensureQueryData(
                            instagramQueries.user.options.list(buildUserParams(ids, publicReportId))
                        )
                    )
                ).then((responses) => responses.flatMap((response) => response.results)),
                Promise.all(
                    getPaginatedIdQueryParam(userIds, BATCH_SIZE).map((ids) =>
                        queryClient.ensureQueryData(
                            instagramQueries.userStatsCache.options.list(buildUserStatsCacheParams(ids, publicReportId))
                        )
                    )
                ).then((responses) => responses.flatMap((response) => response.results)),
            ]);

            return { posts, users, userStats };
        } catch {
            return { posts, users: [], userStats: [] };
        }
    }, [campaignId, publicReportId, queryClient]);

    return {
        posts,
        isPostsLoading: infinitePosts.isLoading,
        isPostsError: infinitePosts.isError,
        userImagesData,
        usersData,
        userStatsData,
        fetchImmediate,
        getIsAccountDataLoading,
        postsTotals,
        areTotalsLoading,
        hasNextPage: infinitePosts.hasNextPage,
        isFetchingNextPage: infinitePosts.isFetchingNextPage,
        fetchNextPage: infinitePosts.fetchNextPage,
    };
}
